/**
 * Security utilities for the login UI
 */

// Generate CSRF token (client-side verification)
export function generateCSRFToken(): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  // Fallback for older browsers
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// Validate CSRF token format
export function isValidCSRFToken(token: string): boolean {
  return /^[a-f0-9]{64}$/.test(token);
}

// Content Security Policy helper
export function getCSPNonce(): string | null {
  const meta = document.querySelector('meta[name="csp-nonce"]');
  return meta?.getAttribute('content') || null;
}

// Rate limiting helper (client-side)
class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();

  isRateLimited(key: string, maxAttempts: number = 5, windowMs: number = 15 * 60 * 1000): boolean {
    const now = Date.now();
    const record = this.attempts.get(key);

    if (!record || now > record.resetTime) {
      this.attempts.set(key, { count: 1, resetTime: now + windowMs });
      return false;
    }

    if (record.count >= maxAttempts) {
      return true;
    }

    record.count++;
    return false;
  }

  getRemainingTime(key: string): number {
    const record = this.attempts.get(key);
    if (!record) return 0;

    const remaining = record.resetTime - Date.now();
    return Math.max(0, Math.ceil(remaining / 1000));
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();

// Input sanitization
export function sanitizeHTML(input: string): string {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

// Secure localStorage wrapper
export class SecureStorage {
  private static prefix = 'agion_auth_';

  static set(key: string, value: unknown, ttl?: number): void {
    const data = {
      value,
      timestamp: Date.now(),
      ttl: ttl ? Date.now() + ttl : null,
    };

    try {
      localStorage.setItem(this.prefix + key, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  static get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;

      const data = JSON.parse(item);

      // Check if expired
      if (data.ttl && Date.now() > data.ttl) {
        this.remove(key);
        return null;
      }

      return data.value;
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return null;
    }
  }

  static remove(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  static clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
}

// Password strength indicator
export function calculatePasswordStrength(password: string): {
  score: number;
  level: 'weak' | 'fair' | 'good' | 'strong';
  suggestions: string[];
} {
  let score = 0;
  const suggestions: string[] = [];

  // Length check
  if (password.length >= 12) score += 2;
  else if (password.length >= 8) score += 1;
  else suggestions.push('Use at least 8 characters');

  // Character variety
  if (/[a-z]/.test(password)) score += 1;
  else suggestions.push('Include lowercase letters');

  if (/[A-Z]/.test(password)) score += 1;
  else suggestions.push('Include uppercase letters');

  if (/\d/.test(password)) score += 1;
  else suggestions.push('Include numbers');

  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  else suggestions.push('Include special characters');

  // Pattern checks
  if (!/(.)\1{2,}/.test(password)) score += 1;
  else suggestions.push('Avoid repeating characters');

  if (!/123|abc|qwe|password|admin/i.test(password)) score += 1;
  else suggestions.push('Avoid common patterns');

  let level: 'weak' | 'fair' | 'good' | 'strong';
  if (score <= 2) level = 'weak';
  else if (score <= 4) level = 'fair';
  else if (score <= 6) level = 'good';
  else level = 'strong';

  return { score, level, suggestions };
}

// WebAuthn utilities
export async function isWebAuthnSupported(): Promise<boolean> {
  return !!(
    window.PublicKeyCredential &&
    PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable &&
    await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  );
}

// Detect if running in secure context
export function isSecureContext(): boolean {
  return window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost';
}

/**
 * Validate redirect URL to prevent open redirect attacks
 * SECURITY: Only allows same-origin redirects or approved external domains
 *
 * @param url - The URL to validate
 * @param allowedDomains - Optional list of allowed external domains (e.g., ['agion.dev', 'auth.agion.dev'])
 * @returns The validated URL if safe, or '/' as fallback
 */
export function validateRedirectUrl(url: string | null | undefined, allowedDomains?: string[]): string {
  // Default to home if no URL provided
  if (!url) return '/';

  // Reject URLs that could be protocol-relative (//evil.com)
  if (url.startsWith('//')) return '/';

  // Allow relative paths starting with /
  if (url.startsWith('/') && !url.startsWith('//')) {
    // Validate only the path segment. OIDC query values legitimately contain
    // percent-encoded redirect URIs, while encoded paths remain unsafe.
    const suffixIndex = url.search(/[?#]/);
    const rawPath = suffixIndex === -1 ? url : url.slice(0, suffixIndex);
    const suffix = suffixIndex === -1 ? '' : url.slice(suffixIndex);
    const normalizedPath = rawPath.replace(/\/+/g, '/');
    if (normalizedPath.includes('..') || normalizedPath.includes('%')) {
      return '/';
    }
    return normalizedPath + suffix;
  }

  // For absolute URLs, verify the origin
  try {
    const parsedUrl = new URL(url, window.location.origin);

    // Same origin is always allowed
    if (parsedUrl.origin === window.location.origin) {
      return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    }

    // Check if it's an allowed external domain
    if (allowedDomains && allowedDomains.length > 0) {
      const hostname = parsedUrl.hostname.toLowerCase();
      for (const domain of allowedDomains) {
        // Allow exact match or subdomain match
        if (hostname === domain.toLowerCase() || hostname.endsWith('.' + domain.toLowerCase())) {
          // Only allow https for external domains (http allowed for localhost dev)
          if (parsedUrl.protocol === 'https:' || hostname === 'localhost') {
            return url;
          }
        }
      }
    }

    // Reject all other URLs
    console.warn('Blocked potentially unsafe redirect URL:', url);
    return '/';
  } catch {
    // Invalid URL format
    console.warn('Invalid redirect URL format:', url);
    return '/';
  }
}

/**
 * Safe redirect function that validates the URL before redirecting
 * SECURITY: Use this instead of direct window.location.href assignment
 */
export function safeRedirect(url: string | null | undefined, allowedDomains?: string[]): void {
  const safeUrl = validateRedirectUrl(url, allowedDomains);
  window.location.href = safeUrl;
}
